import { GraphType } from "@hightouch/lib/query/visual/types";
import { encode } from "src/hooks/use-encoded-search-params";
import {
  defaultExperimentsFormState,
  defaultFormState,
  defaultFunnelChartState,
  defaultInsightsChartState,
} from "src/pages/analytics/state/constants";
import {
  type ChartFormState,
  ChartTab,
  type MetricSelection,
  type SelectedAudience,
  type SelectedAudienceWithBaseline,
  TimeOptions,
} from "src/pages/analytics/types";
import {
  AnalyticsFrequency,
  type RawColumn,
  type RelatedColumn,
  type SyntheticColumn,
} from "src/types/visual";

export type BuildLargeChartUrlParams = {
  parentModelId: number;
  metricSelection?: MetricSelection[];
  selectedAudiences?: SelectedAudience[];
  cohorts?: SelectedAudienceWithBaseline[]; // New way of representing audiences
  groupByColumns?: Array<RawColumn | RelatedColumn | SyntheticColumn>;
  chartTab?: ChartTab;
  graphType?: GraphType;
  rollupFrequency?: AnalyticsFrequency;
  timeValue?: TimeOptions;
  selectedDates?: [Date] | [Date, Date];
  addToDashboardOptions?: {
    dashboard: { name: string; id: string };
  };
};

const DEFAULT_FORM_STATE: Record<ChartTab, ChartFormState> = {
  [ChartTab.Insights]: {
    ...defaultFormState,
    ...defaultInsightsChartState,
  },
  [ChartTab.Funnel]: {
    ...defaultFormState,
    ...defaultFunnelChartState,
  },
  [ChartTab.SplitsMeasurement]: {
    ...defaultFormState,
    ...defaultExperimentsFormState,
  },
};

export const buildChartFormState = ({
  parentModelId,
  selectedAudiences,
  cohorts,
  groupByColumns,
  metricSelection,
  chartTab = ChartTab.Insights,
  graphType = GraphType.Line,
  rollupFrequency = AnalyticsFrequency.All,
  timeValue = TimeOptions.ThirtyDays,
  selectedDates,
  addToDashboardOptions,
}: BuildLargeChartUrlParams): ChartFormState | null => {
  const defaultState = DEFAULT_FORM_STATE[chartTab];

  const formState = {
    ...defaultState,
    parentModelId,
    selectedAudiences: selectedAudiences ?? [],
    cohorts,
    timeValue,
    chartTab,
    graphType,
    rollupFrequency,
    addToDashboardOptions,
  };

  if (selectedDates) {
    formState.timeValue = TimeOptions.Custom;
    formState.selectedDates = selectedDates.map((date) => date.toISOString());
  }
  if (groupByColumns) formState.groupByColumns = groupByColumns;
  if (metricSelection) formState.metricSelection = metricSelection;

  return formState;
};

export const buildLargeChartUrl = (data: BuildLargeChartUrlParams) => {
  const values = buildChartFormState(data);

  return `/analytics/new?data=${encode(values)}`;
};
